<template>
  <div class="peleador-select" ref="rootEl">
    <button type="button" class="ps-trigger" @click="toggle">
      <template v-if="seleccionado">
        <img
          v-if="seleccionado.foto"
          :src="seleccionado.foto"
          class="ps-avatar"
          :alt="seleccionado.nombre"
        />
        <div v-else class="ps-avatar ps-avatar-placeholder">
          {{ seleccionado.nombre?.charAt(0) }}
        </div>
        <span class="ps-label">{{ seleccionado.nombre }} {{ seleccionado.apellido }}</span>
      </template>
      <template v-else-if="allowClear">
        <div class="ps-avatar ps-avatar-placeholder ps-avatar-todos">
          <i class="bi bi-people-fill"></i>
        </div>
        <span class="ps-label ps-placeholder">{{ placeholder }}</span>
      </template>
      <span v-else class="ps-placeholder">{{ placeholder }}</span>
      <i class="bi bi-chevron-down ps-chevron" :class="{ 'is-open': open }"></i>
    </button>

    <div v-if="open" class="ps-panel">
      <div class="ps-search-wrap">
        <i class="bi bi-search ps-search-icon"></i>
        <input
          ref="inputBusqueda"
          v-model="busqueda"
          class="ps-search"
          placeholder="Buscar peleador..."
        />
      </div>
      <ul class="ps-list">
        <li
          v-if="allowClear"
          class="ps-item"
          :class="{ 'is-active': modelValue === null || modelValue === '' }"
          @click="seleccionar(null)"
        >
          <div class="ps-avatar ps-avatar-placeholder ps-avatar-todos">
            <i class="bi bi-people-fill"></i>
          </div>
          <span class="ps-label">Todos los peleadores</span>
          <i v-if="modelValue === null || modelValue === ''" class="bi bi-check-lg ps-check"></i>
        </li>
        <li
          v-for="p in filtrados"
          :key="p.id"
          class="ps-item"
          :class="{ 'is-active': p.id === modelValue }"
          @click="seleccionar(p.id)"
        >
          <img v-if="p.foto" :src="p.foto" class="ps-avatar" :alt="p.nombre" />
          <div v-else class="ps-avatar ps-avatar-placeholder">{{ p.nombre?.charAt(0) }}</div>
          <span class="ps-label">{{ p.nombre }} {{ p.apellido }}</span>
          <i v-if="p.id === modelValue" class="bi bi-check-lg ps-check"></i>
        </li>
        <li v-if="!filtrados.length" class="ps-empty">Sin resultados</li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  modelValue: { type: [Number, String, null], default: null },
  peleadores: { type: Array, default: () => [] },
  placeholder: { type: String, default: "Selecciona un peleador" },
  allowClear: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue"]);

const open = ref(false);
const busqueda = ref("");
const rootEl = ref(null);
const inputBusqueda = ref(null);

const seleccionado = computed(
  () => props.peleadores.find((p) => p.id === props.modelValue) || null
);

const filtrados = computed(() => {
  const q = busqueda.value.trim().toLowerCase();
  if (!q) return props.peleadores;
  return props.peleadores.filter((p) =>
    `${p.nombre} ${p.apellido}`.toLowerCase().includes(q)
  );
});

function toggle() {
  open.value = !open.value;
  if (open.value) {
    busqueda.value = "";
    nextTick(() => inputBusqueda.value?.focus());
  }
}

function seleccionar(id) {
  emit("update:modelValue", id);
  open.value = false;
}

function onDocClick(e) {
  if (rootEl.value && !rootEl.value.contains(e.target)) open.value = false;
}
onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));
</script>

<style scoped>
.peleador-select {
  position: relative;
  width: 100%;
}

/* ── Trigger ── */
.ps-trigger {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  padding: 7px 12px;
  font-size: 13px;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.ps-trigger:hover {
  border-color: rgba(255, 255, 255, 0.3);
}
.peleador-select:has(.ps-panel) .ps-trigger {
  border-color: rgba(243, 188, 44, 0.5);
}
.ps-label {
  flex: 1;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ps-placeholder {
  flex: 1;
  color: rgba(255, 255, 255, 0.4);
}
.ps-chevron {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
  transition: transform 0.15s;
}
.ps-chevron.is-open {
  transform: rotate(180deg);
}

/* ── Avatares ── */
.ps-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.ps-avatar-placeholder {
  background: rgba(243, 188, 44, 0.2);
  color: #f3bc2c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}
.ps-avatar-todos {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
}

/* ── Panel (expande en flujo normal — evita recorte por overflow del modal) ── */
.ps-panel {
  margin-top: 6px;
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}
.ps-search-wrap {
  position: relative;
  padding: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.ps-search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  pointer-events: none;
}
.ps-search {
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 7px;
  color: #fff;
  padding: 6px 10px 6px 28px;
  font-size: 12.5px;
  outline: none;
  font-family: "Inter", sans-serif;
  box-sizing: border-box;
}
.ps-search:focus {
  border-color: rgba(243, 188, 44, 0.5);
}
.ps-search::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

/* Lista — altura limitada a ~6 elementos visibles, el resto hace scroll */
.ps-list {
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 268px;
  overflow-y: auto;
}
.ps-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 8px;
  border-radius: 7px;
  cursor: pointer;
  color: #fff;
  font-size: 13px;
  transition: background 0.12s;
}
.ps-item:hover {
  background: rgba(255, 255, 255, 0.06);
}
.ps-item.is-active {
  background: rgba(243, 188, 44, 0.12);
}
.ps-item.is-active .ps-label {
  color: #f3bc2c;
  font-weight: 600;
}
.ps-check {
  color: #f3bc2c;
  font-size: 14px;
  flex-shrink: 0;
}
.ps-empty {
  padding: 16px 8px;
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  font-size: 12.5px;
}
</style>
